/**
 * App Shell — Sidebar + Topbar + área de conteúdo + roteamento.
 * Porta fielmente o design de app.jsx / screen-kit.jsx / tenant-bu.jsx.
 *
 * Workstream B: dados reais de BU e tenant via hooks (ActiveScopeProvider + useTenantQuery).
 * Workstream H: busca global real no CommandPalette (contas + oportunidades).
 * TASK-22: CommandPalette extraído para features/copilot com filtro RBAC e a11y completa.
 */

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@azim-crm/icons";
import { AzimWordmark, AzimMark } from "@azim-crm/icons";
import { Button, Avatar, Modal, Input } from "@azim-crm/ui-components";
import { useAuth } from "./AuthProvider.js";
import { useTheme } from "./ThemeProvider.js";
import { useActiveScope } from "./providers/ActiveScopeProvider.js";
import { useTenantQuery } from "./providers/QueryProvider.js";
import { BrandingApplier } from "../features/platform/BrandingApplier.js";
import { usePublicBranding } from "../features/platform/hooks.js";
import {
  listBusinessUnits,
  getTenant,
  type PortalBusinessUnit,
  type PortalTenant,
} from "../data/repos/platformRepo.js";
import { ROLE_LABELS, type UserRole } from "../types/domain.js";
import { CommandPalette } from "../features/copilot/CommandPalette.js";
import { CopilotDrawer } from "../features/copilot/CopilotDrawer.js";
import { usePendingRecordings } from "../features/call-recordings/hooks.js";
import { useInbox } from "../features/inbox/hooks.js";
import { useMyDay } from "../features/activities/hooks.js";
import type { RbacContext } from "../shared/rbac/index.js";
import { canSeeRoute, type RouteId } from "../shared/rbac/rbac.js";
import { isFeatureAvailable } from "../shared/features/featureAvailability.js";
import styles from "./Shell.module.css";

// ---- Nav items -----------------------------------------------------------
const NAV = [
  { id: "dashboard", label: "Visão geral", icon: "dashboard", path: "/" },
  { id: "pipeline", label: "Pipeline", icon: "kanban", path: "/pipeline" },
  { id: "partners", label: "Parcerias", icon: "briefcase", path: "/partners" },
  { id: "accounts", label: "Contas", icon: "accounts", path: "/accounts" },
  { id: "activities", label: "Atividades", icon: "activities", path: "/activities" },
  { id: "inbox", label: "Caixa de entrada", icon: "inbox", path: "/inbox" },
  { id: "conversations", label: "Atendimento", icon: "message", path: "/conversations" },
  { id: "recordings", label: "Gravações", icon: "phone", path: "/recordings" },
  { id: "reports", label: "Relatórios", icon: "reports", path: "/reports" },
] as const;

const ADMIN_NAV = [
  { id: "admin", label: "Administração", icon: "shieldCheck", path: "/admin" },
  { id: "queues", label: "Filas", icon: "inbox", path: "/queues" },
  { id: "channels", label: "Canais", icon: "plug", path: "/channels" },
  { id: "workflows", label: "Automações", icon: "layers", path: "/workflows" },
  { id: "platform", label: "Plataforma", icon: "layers", path: "/platform" },
  { id: "settings", label: "Configurações", icon: "settings", path: "/settings" },
] as const;

type NavIcon = typeof NAV[number]["icon"] | typeof ADMIN_NAV[number]["icon"];

// ---- Paleta determinística de swatch para BU --------------------------------
/**
 * Deriva uma cor de swatch determinística a partir de uma string (id ou nome da BU).
 * Usa hash DJB2 simples para distribuição uniforme na paleta de tokens do projeto.
 * Garante que a mesma BU sempre receba a mesma cor — sem campo `tone` no backend.
 */
const BU_SWATCH_PALETTE = [
  "var(--lagoa-500)",
  "var(--vellus-lagoa)",
  "#7c3aed", // violeta
  "#0ea5e9", // sky
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#06b6d4", // cyan
  "#84cc16", // lime
];

function hashString(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
    h = h >>> 0; // converte para uint32
  }
  return h;
}

function buSwatchColor(bu: PortalBusinessUnit): string {
  if (bu.tone) return bu.tone;
  const idx = hashString(bu.id || bu.name) % BU_SWATCH_PALETTE.length;
  return BU_SWATCH_PALETTE[idx] ?? "var(--lagoa-500)";
}

/** Derivação do "short" (abreviação) da BU a partir do nome completo. */
function buShortLabel(bu: PortalBusinessUnit): string {
  if (bu.short) return bu.short;
  // Pega as primeiras letras de até 2 palavras: "BU Comercial SP" → "BC"
  const words = bu.name.trim().split(/\s+/);
  if (words.length === 1) return (words[0] ?? "").slice(0, 2).toUpperCase();
  return ((words[0] ?? "").charAt(0) + (words[1]?.charAt(0) ?? "")).toUpperCase();
}

// ---- Utilitários de usuário ------------------------------------------------

function userInitials(displayName: string, email: string): string {
  const name = displayName || email;
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return ((parts[0] ?? "").charAt(0) + (parts[parts.length - 1] ?? "").charAt(0)).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function userAvatarColor(email: string): string {
  const palette = [
    "var(--lagoa-500)",
    "#7c3aed",
    "#0ea5e9",
    "#10b981",
    "#f59e0b",
    "#ef4444",
  ];
  const idx = hashString(email) % palette.length;
  return palette[idx] ?? "var(--lagoa-500)";
}

// ---- AccountLogoMini mini (sem dependência extra) ----------------------------
// Identidade do TENANT (marca do cliente) — distinta de AzimMark/AzimWordmark, que são a marca
// do produto Azim e nunca são substituídas por branding de tenant (o lockup "Azim BY <tenant>"
// é deliberado, e é o que a captura de tela do dono do produto mostra). Quando o tenant tem
// `logoUrl` configurado, renderiza a imagem; sem `logoUrl`, ou se a imagem falhar ao carregar
// (arquivo removido do storage, por exemplo), cai para as iniciais sobre a cor determinística —
// nunca um ícone de imagem quebrada.
function AccountLogoMini({
  name,
  tone,
  size = 38,
  logoUrl,
}: {
  name: string;
  tone: string;
  size?: number;
  logoUrl?: string | null | undefined;
}) {
  const initials = name.slice(0, 2).toUpperCase();
  const [imgFailed, setImgFailed] = useState(false);
  const dimensions = {
    width: size,
    height: size,
    borderRadius: Math.round(size * 0.28),
    flexShrink: 0,
  } as const;

  if (logoUrl && !imgFailed) {
    return (
      <img
        src={logoUrl}
        alt=""
        aria-hidden="true"
        style={{ ...dimensions, objectFit: "cover", background: tone }}
        onError={() => setImgFailed(true)}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      style={{
        ...dimensions,
        background: tone,
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: Math.round(size * 0.37),
        fontWeight: 700,
        letterSpacing: "-0.03em",
      }}
    >
      {initials}
    </span>
  );
}

// ---- BU Selector (dados reais) -------------------------------------------
function BUSelector() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { businessUnitId, setBusinessUnit } = useActiveScope();
  const { fireToast } = useSidebarToast();

  // Lista real de BUs via useTenantQuery
  const { data: buList } = useTenantQuery<readonly PortalBusinessUnit[]>(
    "businessUnits",
    listBusinessUnits,
  );

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", handler);
    return () => window.removeEventListener("mousedown", handler);
  }, [open]);

  const currentBU = buList?.find((b) => b.id === businessUnitId) ?? buList?.[0];

  const currentShort = currentBU ? buShortLabel(currentBU) : "BU";
  const currentColor = currentBU ? buSwatchColor(currentBU) : "var(--bg-muted)";

  const handlePick = (bu: PortalBusinessUnit) => {
    setOpen(false);
    setBusinessUnit(bu.id);
    fireToast(`Business Unit: ${bu.name}`);
  };

  return (
    <div ref={ref} className={styles.buWrapper}>
      <button
        type="button"
        className={[styles.buButton, open ? styles.buButtonOpen : ""].join(" ")}
        onClick={() => setOpen((o) => !o)}
        aria-label={`Business Unit: ${currentShort}. Clique para trocar.`}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={styles.buSwatch} style={{ background: currentColor }} />
        <span>BU: {currentShort}</span>
        <Icon name="chevronDown" size={16} />
      </button>
      {open && (
        <div className={styles.buDropdown} role="listbox" aria-label="Selecionar Business Unit">
          <div className={`t-eyebrow ${styles.navSection}`} style={{ padding: "8px 10px 6px" }}>
            Business Unit
          </div>
          {!buList || buList.length === 0 ? (
            <div style={{ padding: "12px 10px", color: "var(--fg-3)", fontSize: 13 }}>
              Nenhuma BU disponível
            </div>
          ) : (
            buList.map((b) => {
              const isActive = b.id === (currentBU?.id ?? "");
              const color = buSwatchColor(b);
              return (
                <button
                  key={b.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={[styles.buOption, isActive ? styles.buOptionActive : ""].join(" ")}
                  onClick={() => handlePick(b)}
                >
                  <span style={{ width: 16, height: 16, borderRadius: 5, background: color, flexShrink: 0 }} />
                  <span className={[styles.buOptionName, isActive ? styles.buOptionNameActive : ""].join(" ")}>
                    {b.name}
                  </span>
                  {isActive && <Icon name="check" size={16} style={{ color: "var(--fg-accent)" }} />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// ---- Contexto de toast (para BUSelector interno acionar o toast do Shell) ---

interface SidebarToastContextValue {
  fireToast(msg: string): void;
}
const SidebarToastContext = createContext<SidebarToastContextValue | null>(null);

function useSidebarToast(): SidebarToastContextValue {
  const ctx = useContext(SidebarToastContext);
  if (!ctx) throw new Error("useSidebarToast fora do SidebarToastContext");
  return ctx;
}

// ---- Tenant Modal (dados reais) ------------------------------------------
function TenantModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose(): void;
}) {
  const [query, setQuery] = useState("");

  const { data: tenant } = useTenantQuery<PortalTenant>(
    "tenants",
    getTenant,
    { enabled: open },
  );
  // Endpoint público (brand.json) — não RequiresTenantAdmin, ver achado da revisão adversarial
  // em usePublicBranding(). O seletor de tenant é visível para todo usuário autenticado.
  const { branding } = usePublicBranding();

  useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  // Filtra o tenant único pelo query (busca por displayName ou slug)
  const matchesTenant =
    !tenant ||
    !query ||
    tenant.displayName.toLowerCase().includes(query.toLowerCase()) ||
    tenant.slug.toLowerCase().includes(query.toLowerCase());

  // Cor determinística para o avatar do tenant
  const tenantColor = tenant ? buSwatchColor({ id: tenant.id, name: tenant.displayName, active: true }) : "var(--bg-muted)";

  return (
    <Modal open={open} onClose={onClose} width={620}>
      <div className={styles.tenantModal}>
        <div className={styles.tenantModalHeader}>
          <h3 className={styles.tenantModalTitle}>Tenant atual</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{ border: "none", background: "none", cursor: "pointer", display: "flex", padding: 6, borderRadius: "var(--radius-sm)" }}
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className={styles.tenantModalBody}>
          <div className={styles.tenantModalSearch}>
            <Input
              icon="search"
              placeholder="Buscar tenant…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className={styles.tenantList} role="list">
            {!tenant ? (
              <div className={styles.tenantEmpty}>Carregando…</div>
            ) : !matchesTenant ? (
              <div className={styles.tenantEmpty}>Nenhum tenant encontrado</div>
            ) : (
              <div
                role="listitem"
                className={[styles.tenantItem, styles.tenantItemCurrent].join(" ")}
              >
                <AccountLogoMini name={tenant.displayName} tone={tenantColor} size={46} logoUrl={branding?.logoUrl} />
                <div className={styles.tenantItemInfo}>
                  <div className={styles.tenantItemName}>{tenant.displayName}</div>
                  <div className={styles.tenantItemMeta}>
                    {tenant.slug} · {tenant.timezone}
                  </div>
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: "var(--success-fg)", display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <Icon name="check" size={14} /> Atual
                </span>
              </div>
            )}
          </div>
        </div>
        <div className={styles.tenantModalFooter}>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ---- Sidebar -------------------------------------------------------------
function Sidebar({
  collapsed,
  setCollapsed,
  onSearch,
  onCopilot,
  onNavigate,
  activePath,
  onTenantOpen,
  showTenantBlock,
}: {
  collapsed: boolean;
  setCollapsed(v: boolean): void;
  onSearch(): void;
  onCopilot(): void;
  onNavigate(path: string): void;
  activePath: string;
  onTenantOpen(): void;
  showTenantBlock: boolean;
}) {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();

  // Tenant real via useTenantQuery
  const { data: tenant } = useTenantQuery<PortalTenant>("tenants", getTenant);

  // Branding do tenant (cores já aplicadas por BrandingApplier; aqui só o logoUrl, para o
  // slot de identidade do tenant na sidebar — ver AccountLogoMini). Endpoint público
  // (brand.json), não RequiresTenantAdmin — a sidebar é visível para todo usuário autenticado,
  // não só TenantAdmin (achado da revisão adversarial, ver usePublicBranding()).
  const { branding } = usePublicBranding();

  // Contagem de gravações pendentes para o badge dinâmico na nav (Parte B).
  // Fail-safe: se a query falhar, o badge simplesmente não aparece.
  const { recordings: pendingRecordingsData } = usePendingRecordings();
  const pendingRecordingsCount = pendingRecordingsData?.total ?? 0;

  // Contadores da nav. Antes eram o literal `2` em Atividades e Caixa de entrada — número de
  // protótipo que sobreviveu à promoção para dados reais e passava a impressão de haver itens
  // pendentes num inbox vazio. Mesma disciplina do badge de gravações: se a query falhar, o
  // contador some em vez de mostrar um valor inventado.
  const { items: inboxItems } = useInbox();
  const unreadInboxCount = inboxItems.filter((i) => i.unread).length;

  // As quatro seções abertas do Meu Dia são disjuntas por construção no servidor
  // (GetMyDayQuery: stale, migrada-e-não-stale, tarefa de hoje sem migração, compromisso de
  // hoje), então somá-las não conta ninguém duas vezes. `completedToday` fica de fora: já foi
  // resolvido e não é pendência.
  const { data: myDay } = useMyDay();
  const openActivitiesCount = myDay
    ? myDay.staleReview.length + myDay.migrated.length + myDay.today.length + myDay.appointments.length
    : 0;

  const displayName = user?.displayName ?? user?.email ?? "Usuário";
  const email = user?.email ?? "";
  const role = user?.role;
  const initials = userInitials(displayName, email);
  const avatarColor = userAvatarColor(email);
  const roleLabel = role ? (ROLE_LABELS[role] ?? role) : "";

  const tenantColor = tenant
    ? buSwatchColor({ id: tenant.id, name: tenant.displayName, active: true })
    : "var(--bg-muted)";
  const tenantName = tenant?.displayName ?? "—";
  const tenantMeta = tenant ? `${tenant.slug} · ${tenant.timezone}` : "—";

  const rootPath = (p: string) => {
    if (p.startsWith("/account/")) return "/accounts";
    if (p.startsWith("/opportunity/")) return "/pipeline";
    if (p.startsWith("/partner/")) return "/partners";
    if (p.startsWith("/recording/")) return "/recordings";
    return p;
  };
  const active = rootPath(activePath);

  const nav = (path: string) => onNavigate(path);

  return (
    <aside
      className={[styles.sidebar, collapsed ? styles.sidebarCollapsed : ""].join(" ")}
      aria-label="Navegação principal"
    >
      {/* Brand */}
      <div
        className={[styles.sidebarBrand, collapsed ? styles.sidebarBrandCollapsed : ""].join(" ")}
      >
        <button
          type="button"
          className={styles.brandButton}
          onClick={() => nav("/")}
          aria-label="Azim CRM — ir para visão geral"
        >
          {collapsed ? (
            <AzimMark size={30} variant="tile" />
          ) : (
            <AzimWordmark size={19} variant="tile" byline />
          )}
        </button>
        {!collapsed && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              aria-label="Recolher menu"
              style={{ border: "none", background: "none", cursor: "pointer", display: "flex", padding: 6, borderRadius: "var(--radius-sm)", color: "var(--fg-3)" }}
            >
              <Icon name="chevronsLeft" size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Tenant block (master e tenant_admin) */}
      {showTenantBlock && tenant && (
        <>
          {collapsed ? (
            <div className={styles.tenantLogoCollapsed}>
              <button
                type="button"
                onClick={onTenantOpen}
                title={`Tenant atual · ${tenantName}`}
                aria-label={`Tenant atual: ${tenantName}. Clique para ver.`}
                style={{ border: "none", background: "none", cursor: "pointer", padding: 0 }}
              >
                <AccountLogoMini name={tenantName} tone={tenantColor} size={34} logoUrl={branding?.logoUrl} />
              </button>
            </div>
          ) : (
            <div className={styles.tenantBlock}>
              <button
                type="button"
                className={styles.tenantButton}
                onClick={onTenantOpen}
                aria-label={`Tenant atual: ${tenantName}. Clique para ver.`}
              >
                <AccountLogoMini name={tenantName} tone={tenantColor} size={38} logoUrl={branding?.logoUrl} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className={styles.tenantName}>{tenantName}</div>
                  <div className={styles.tenantMeta}>{tenantMeta}</div>
                </div>
                <Icon name="chevronsUpDown" size={17} style={{ color: "var(--fg-3)", flexShrink: 0 }} />
              </button>
            </div>
          )}
        </>
      )}

      {/* New + Search */}
      <div className={[styles.sidebarActions, collapsed ? styles.sidebarActionsCollapsed : ""].join(" ")}>
        {collapsed ? (
          <button
            type="button"
            onClick={() => nav("/pipeline")}
            title="Criar"
            aria-label="Criar nova oportunidade"
            style={{ border: "1px solid var(--border-2)", background: "none", cursor: "pointer", display: "flex", padding: 8, borderRadius: "var(--radius-md)", color: "var(--fg-2)" }}
          >
            <Icon name="plus" size={16} />
          </button>
        ) : (
          <Button variant="primary" icon="plus" full onClick={() => nav("/pipeline")}>
            Criar
          </Button>
        )}
        {collapsed ? (
          <button
            type="button"
            onClick={onSearch}
            title="Buscar (⌘K)"
            aria-label="Buscar"
            style={{ border: "none", background: "none", cursor: "pointer", display: "flex", padding: 8, borderRadius: "var(--radius-sm)", color: "var(--fg-3)" }}
          >
            <Icon name="search" size={16} />
          </button>
        ) : (
          <button
            type="button"
            className={styles.searchButton}
            onClick={onSearch}
            aria-label="Buscar contas, oportunidades… (⌘K)"
          >
            <Icon name="search" size={15} />
            <span className={styles.searchLabel}>Buscar…</span>
            <span className={styles.kbd}>⌘K</span>
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className={styles.nav} aria-label="Menu comercial">
        {!collapsed && (
          <span className={`t-eyebrow ${styles.navSection}`}>Comercial</span>
        )}
        {/*
          Filtro de disponibilidade de funcionalidade (não de papel): esconde do
          menu as telas que o ambiente ainda não oferece — hoje "Atendimento".
          O filtro por papel do NAV comercial permanece como está; ver
          shared/features/featureAvailability para o mecanismo e a configuração.
        */}
        {NAV.filter((n) => isFeatureAvailable(n.id)).map((n) => {
          const isActive = active === n.path;
          // Todo badge da nav é contagem real; zero não renderiza nada.
          const counts: Partial<Record<string, number>> = {
            recordings: pendingRecordingsCount,
            inbox: unreadInboxCount,
            activities: openActivitiesCount,
          };
          const count = counts[n.id] ?? 0;
          const badge = count > 0 ? count : undefined;
          return (
            <button
              key={n.id}
              type="button"
              onClick={() => nav(n.path)}
              title={n.label}
              aria-label={n.label}
              aria-current={isActive ? "page" : undefined}
              className={[
                styles.navItem,
                collapsed ? styles.navItemCollapsed : "",
                isActive ? styles.navItemActive : "",
              ].join(" ")}
            >
              {isActive && !collapsed && <span className={styles.navActiveBar} aria-hidden="true" />}
              <Icon name={n.icon as NavIcon} size={18} />
              {!collapsed && <span className={styles.navLabel}>{n.label}</span>}
              {!collapsed && badge != null && (
                <span
                  className={[styles.navBadge, isActive ? styles.navBadgeActive : styles.navBadgeInactive].join(" ")}
                >
                  {badge}
                </span>
              )}
              {collapsed && badge != null && (
                <span className={styles.navBadgeDot} aria-hidden="true" />
              )}
            </button>
          );
        })}

        {!collapsed && (
          <span className={`t-eyebrow ${styles.navSection}`}>Administração</span>
        )}
        {ADMIN_NAV.filter((n) => {
          // SEC-002: oculta itens de nav que o papel atual não pode ver.
          // Usa canSeeRoute para respeitar ROUTE_EXCLUDED_ROLES (ex.: platform_operator → workflows).
          const rbacForNav: RbacContext = {
            roles: role ? [role] : [],
            isMaster: role === "platform_operator",
            canSwitchBU: role === "platform_operator" || role === "tenant_admin",
          };
          return canSeeRoute(n.id as RouteId, rbacForNav);
        }).map((n) => {
          const isActive = active === n.path;
          return (
            <button
              key={n.id}
              type="button"
              onClick={() => nav(n.path)}
              title={n.label}
              aria-label={n.label}
              aria-current={isActive ? "page" : undefined}
              className={[
                styles.navItem,
                collapsed ? styles.navItemCollapsed : "",
                isActive ? styles.navItemActive : "",
              ].join(" ")}
            >
              {isActive && !collapsed && <span className={styles.navActiveBar} aria-hidden="true" />}
              <Icon name={n.icon as NavIcon} size={18} />
              {!collapsed && <span className={styles.navLabel}>{n.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* AI CTA — abre o CopilotDrawer (⌘J, TASK-21, Req 9) */}
      <div className={collapsed ? styles.aiCtaCollapsed : styles.aiCta}>
        {collapsed ? (
          <button
            type="button"
            title="Azim AI (⌘J)"
            aria-label="Abrir Azim AI (⌘J)"
            onClick={onCopilot}
            style={{ border: "1px solid var(--border-accent)", background: "var(--bg-accent-soft)", cursor: "pointer", display: "flex", padding: 8, borderRadius: "var(--radius-md)", color: "var(--fg-accent)" }}
          >
            <Icon name="sparkles" size={18} />
          </button>
        ) : (
          <button type="button" className={styles.aiCtaButton} aria-label="Abrir Azim AI (⌘J)" onClick={onCopilot}>
            <span className={styles.aiIcon} aria-hidden="true">
              <Icon name="sparkles" size={16} />
            </span>
            <span className={styles.aiTextGroup}>
              <span className={styles.aiTitleRow}>
                <span className={styles.aiTitle}>Azim AI</span>
                <span className={styles.aiSoonBadge}>Em breve</span>
              </span>
              <span className={styles.aiSub}>Copiloto comercial</span>
            </span>
          </button>
        )}
      </div>

      {/* Footer */}
      <div className={[styles.sidebarFooter, collapsed ? styles.sidebarFooterCollapsed : ""].join(" ")}>
        <div className={[styles.footerRow, collapsed ? styles.footerRowCollapsed : ""].join(" ")}>
          {!collapsed && (
            <button
              type="button"
              className={styles.userButton}
              onClick={() => nav("/settings")}
              aria-label="Configurações da conta"
            >
              <Avatar
                name={displayName}
                initials={initials}
                color={avatarColor}
                size={30}
              />
              <div className={styles.userInfo}>
                <div className={styles.userName}>{displayName}</div>
                <div className={styles.userRole}>{roleLabel}</div>
              </div>
            </button>
          )}
          <div className={styles.footerActions}>
            <button
              type="button"
              title={theme === "dark" ? "Tema claro" : "Tema escuro"}
              aria-label={theme === "dark" ? "Ativar tema claro" : "Ativar tema escuro"}
              onClick={toggleTheme}
              style={{ border: "none", background: "none", cursor: "pointer", display: "flex", padding: 6, borderRadius: "var(--radius-sm)", color: "var(--fg-3)" }}
            >
              <Icon name={theme === "dark" ? "sun" : "moon"} size={16} />
            </button>
            {!collapsed && (
              <button
                type="button"
                title="Sair"
                aria-label="Sair da conta"
                onClick={logout}
                style={{ border: "none", background: "none", cursor: "pointer", display: "flex", padding: 6, borderRadius: "var(--radius-sm)", color: "var(--fg-3)" }}
              >
                <Icon name="logout" size={16} />
              </button>
            )}
          </div>
        </div>
        {collapsed && (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            title="Expandir menu"
            aria-label="Expandir menu"
            style={{ border: "none", background: "none", cursor: "pointer", display: "flex", margin: "0 auto", padding: 6, borderRadius: "var(--radius-sm)", color: "var(--fg-3)" }}
          >
            <Icon name="chevronRight" size={16} />
          </button>
        )}
      </div>
    </aside>
  );
}

// ---- Topbar --------------------------------------------------------------
function Topbar({
  onSearch,
  onCopilot,
  canSwitchBU,
}: {
  onSearch(): void;
  onCopilot(): void;
  canSwitchBU: boolean;
}) {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const displayName = user?.displayName ?? user?.email ?? "Usuário";
  const email = user?.email ?? "";
  const initials = userInitials(displayName, email);
  const avatarColor = userAvatarColor(email);

  return (
    <header className={styles.topbar}>
      <button
        type="button"
        className={styles.topbarSearch}
        onClick={onSearch}
        aria-label="Buscar contas, oportunidades… (⌘K)"
      >
        <Icon name="search" size={15} />
        <span className={styles.topbarSearchLabel}>
          Buscar contas, oportunidades…
        </span>
        <span className={styles.kbd}>⌘K</span>
      </button>

      <span className={styles.topbarSpacer} />

      {canSwitchBU && <BUSelector />}

      <Button variant="secondary" size="sm" icon="sparkles" onClick={onCopilot}>
        Azim AI
      </Button>

      <button
        type="button"
        title={theme === "dark" ? "Tema claro" : "Tema escuro"}
        aria-label={theme === "dark" ? "Ativar tema claro" : "Ativar tema escuro"}
        onClick={toggleTheme}
        style={{ border: "none", background: "none", cursor: "pointer", display: "flex", padding: 6, borderRadius: "var(--radius-sm)", color: "var(--fg-3)" }}
      >
        <Icon name={theme === "dark" ? "sun" : "moon"} size={18} />
      </button>

      <div className={styles.notifWrapper}>
        <button
          type="button"
          title="Notificações"
          aria-label="Notificações"
          onClick={() => navigate("/notifications")}
          style={{ border: "none", background: "none", cursor: "pointer", display: "flex", padding: 6, borderRadius: "var(--radius-sm)", color: "var(--fg-3)" }}
        >
          <Icon name="bell" size={18} />
        </button>
        <span className={styles.notifDot} aria-label="Há notificações não lidas" />
      </div>

      <div className={styles.topbarDivider} aria-hidden="true" />

      <button
        type="button"
        className={styles.avatarButton}
        onClick={() => navigate("/settings")}
        aria-label="Configurações da conta"
      >
        <Avatar
          name={displayName}
          initials={initials}
          color={avatarColor}
          size={30}
        />
      </button>
    </header>
  );
}

// ---- Toast ---------------------------------------------------------------
function Toast({ message }: { message: string }) {
  return (
    <div className={styles.toast} role="status" aria-live="polite">
      <span className={styles.toastIcon}>
        <Icon name="check" size={16} />
      </span>
      {message}
    </div>
  );
}

// ---- Shell (exported) ----------------------------------------------------
export function Shell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [tenantModalOpen, setTenantModalOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);

  // Acompanha o viewport tablet (≤ 768px): recolhe a sidebar ao entrar no breakpoint
  // e a expande ao voltar para desktop, evitando que fique travada recolhida — RNF 12, seção 15.1.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    if (mq.matches) setCollapsed(true);
    const handleChange = (e: MediaQueryListEvent) => {
      setCollapsed(e.matches);
    };
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);

  // Derivação de permissões a partir do papel REAL do usuário (ADR-0015)
  const role: UserRole | undefined = user?.role;
  const canSwitchBU = role === "tenant_admin" || role === "platform_operator";

  /**
   * Contexto RBAC derivado do papel do usuário autenticado.
   * `isMaster` = platform_operator enxerga tudo (ADR-0015).
   * Passado ao CommandPalette para filtragem de itens (Req 7.4).
   */
  const rbacCtx: RbacContext = useMemo(() => ({
    roles: role ? [role] : [],
    isMaster: role === "platform_operator",
    canSwitchBU,
  }), [role, canSwitchBU]);

  const fireToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2400);
  }, []);

  // ⌘K shortcut — abre/fecha a palette (Req 7, RNF 9.3)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // ⌘J / Ctrl+J shortcut — abre/fecha o CopilotDrawer (Req 9, design.md §20.15, TASK-21)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setCopilotOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <SidebarToastContext.Provider value={{ fireToast }}>
      {/* Aplica o branding white-label do tenant (cores → CSS custom properties). */}
      <BrandingApplier />
      <div className={styles.shell}>
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          onSearch={() => setPaletteOpen(true)}
          onCopilot={() => setCopilotOpen((o) => !o)}
          onNavigate={(path) => navigate(path)}
          activePath={location.pathname}
          onTenantOpen={() => setTenantModalOpen(true)}
          showTenantBlock={canSwitchBU}
        />
        <div className={styles.main}>
          <Topbar
            onSearch={() => setPaletteOpen(true)}
            onCopilot={() => setCopilotOpen((o) => !o)}
            canSwitchBU={canSwitchBU}
          />
          <main id="az-main" className={styles.content}>
            <div className={styles.contentInner}>{children}</div>
          </main>
        </div>

        <TenantModal
          open={tenantModalOpen}
          onClose={() => setTenantModalOpen(false)}
        />

        {/* Command palette com filtro RBAC, ações rápidas e a11y (TASK-22) */}
        {paletteOpen && (
          <CommandPalette
            open={paletteOpen}
            rbacCtx={rbacCtx}
            onClose={() => setPaletteOpen(false)}
            onNavigate={(path) => { setPaletteOpen(false); navigate(path); }}
          />
        )}

        {/* CopilotDrawer com SSE, state machine e degradação graciosa (TASK-21, Req 9) */}
        {copilotOpen && (
          <CopilotDrawer onClose={() => setCopilotOpen(false)} />
        )}

        {toast && <Toast message={toast} />}
      </div>
    </SidebarToastContext.Provider>
  );
}

// CommandPalette foi extraído para features/copilot/CommandPalette.tsx (TASK-22).
// Importado acima como: import { CommandPalette } from "../features/copilot/CommandPalette.js";
